import { WFS, GeoJSON } from 'ol/format'
import { Stroke, Style, Fill, Circle } from 'ol/style'
import VectorLayer from 'ol/layer/Vector'
import VectorWFS from './ol/source/VectorWFS'
import { isArcGisLayer, isArcGisWfsLayer } from './identify/isArcGisLayer'
import Polygon from 'jsts-ie/org/locationtech/jts/geom/Polygon'
import MultiPolygon from 'jsts-ie/org/locationtech/jts/geom/MultiPolygon'
import Polygonizer from 'jsts-ie/org/locationtech/jts/operation/polygonize/Polygonizer'
import LinearRing from 'jsts-ie/org/locationtech/jts/geom/LinearRing'
import GeoJSONParser from 'jsts-ie/org/locationtech/jts/io/GeoJSONParser'

import 'jsts-ie/org/locationtech/jts/monkey.js' // Monkey patch jsts objects to include methods on geometry instead of going through static methods

export function getSelectStyleFunction() {
  var styles = createDefaultEditingStyles()
  styles['Polygon'] = styles['LineString']
  styles['MultiPolygon'] = styles['LineString']
  return function(feature, resolution) {
    return styles[feature.getGeometry().getType()]
  }
}

export function getCopyStyleFunction() {
  var styles = createDefaultEditingStyles()
  styles['Polygon'] = styles['LineString'].concat(styles['Polygon'])
  styles['MultiPolygon'] = styles['LineString'].concat(styles['MultiPolygon'])

  return function(feature, resolution) {
    return styles[feature.getGeometry().getType()]
  }
}

export const createInvisibleVectorLayer = config => {
  const source = (() => {
    if (isArcGisLayer(config) && !isArcGisWfsLayer(config)) {
      throw new Error('Cannot create vector layer for non-wfs layer')
    }

    const ags = isArcGisWfsLayer(config)

    if (config.type !== 'Vector') {
      const params = {
        typename: config.metadata.featureType,
        outputFormat: ags ? 'GML3' : 'application/json',
        version: '2.0.0'
      }
      if (config.source.params.CQL_FILTER || config.source.params.cql_filter) {
        params.ecql_filter =
          config.source.params.CQL_FILTER || config.source.params.cql_filter
      }

      if (config.source.params.viewparams) {
        params.VIEWPARAMS = config.source.params.viewparams
      }

      if (config.source.params.access_token) {
        params.access_token = config.source.params.access_token
      }

      return new VectorWFS({
        params,
        ratio: 1,
        type: 'VectorWFS',
        format: ags ? new WFS() : new GeoJSON(),
        url: config.metadata.baseUrl || 'https://olgatest.scaskog.com/ows/wfs'
      })
    } else {
      return l.getSource()
    }
  })()

  const layer = new VectorLayer({
    source,
    maxResolution: config.maxResolution,
    minResolution: config.minResolution,
    visible: true,
    style: new Style({
      stroke: new Stroke({
        color: 'rgba(0, 0, 0, 0)'
      }),
      fill: new Fill({
        color: 'rgba(0, 0, 0, 0)'
      })
    })
  })
  layer.set('invisibleLayer_', true)
  return layer
}

export const isInvisibleLayer = layer => layer.get('invisibleLayer_')

export function createDefaultEditingStyles() {
  var styles = {}
  var white = [255, 255, 255, 1]
  var blue = [0, 153, 255, 1]
  var width = 3
  styles['Polygon'] = [
    new Style({
      fill: new Fill({
        color: [255, 255, 255, 0.5]
      })
    })
  ]
  styles['MultiPolygon'] = styles['Polygon']

  styles['LineString'] = [
    new Style({
      stroke: new Stroke({
        color: white,
        width: width + 2
      })
    }),
    new Style({
      stroke: new Stroke({
        color: blue,
        width: width
      })
    })
  ]
  styles['MultiLineString'] = styles['LineString']

  styles['Circle'] = styles['Polygon'].concat(styles['LineString'])

  styles['Point'] = [
    new Style({
      image: new Circle({
        radius: width * 2,
        fill: new Fill({
          color: blue
        }),
        stroke: new Stroke({
          color: white,
          width: width / 2
        })
      }),
      zIndex: Infinity
    })
  ]
  styles['MultiPoint'] = styles['Point']

  styles['GeometryCollection'] = styles['Polygon'].concat(
    styles['LineString'],
    styles['Point']
  )

  return styles
}

const parser = new GeoJSONParser()
const geojsonFormat = new GeoJSON()

// Converts a jsts geometry to a valid geometry
const toValidGeom = (geom, buff) => {
  if (geom instanceof Polygon) {
    if (buff) {
      geom = geom.buffer(0.001)
    }

    if (geom.isValid()) {
      geom.normalize()
      return geom
    }
    const polygonizer = new Polygonizer()
    addPolygon(geom, polygonizer)
    return toPolygonGeometry(polygonizer.getPolygons())
  } else if (geom instanceof MultiPolygon) {
    if (buff) {
      geom = geom.buffer(0.001)
    }
    if (geom.isValid()) {
      geom.normalize()
      return geom
    }
    const polygonizer = new Polygonizer()
    for (let n = geom.getNumGeometries(); n-- > 0; ) {
      addPolygon(geom.getGeometryN(n), polygonizer)
    }
    return toPolygonGeometry(polygonizer.getPolygons())
  } else {
    return geom
  }
}

const addPolygon = (polygon, polygonizer) => {
  addLineString(polygon.getExteriorRing(), polygonizer)
  for (let n = polygon.getNumInteriorRing(); n-- > 0; ) {
    addLineString(polygon.getInteriorRingN(n), polygonizer)
  }
}

const addLineString = (lineString, polygonizer) => {
  if (lineString instanceof LinearRing) {
    lineString = lineString
      .getFactory()
      .createLineString(lineString.getCoordinateSequence())
  }

  const point = lineString
    .getFactory()
    .createPoint(lineString.getCoordinateN(0))
  const toAdd = lineString.union(point)

  polygonizer.add(toAdd)
}

const toPolygonGeometry = polygons => {
  switch (polygons.size()) {
    case 0:
      return null
    case 1:
      return polygons.iterator().next()
    default:
      const iter = polygons.iterator()
      let ret = iter.next()
      while (iter.hasNext()) {
        ret = ret.symDifference(iter.next())
      }
      return ret
  }
}

// Converts a Openlayers geometry to a valid geometry
export const toValidGeometry = olGeometry => {
  const geojsonGeometry = geojsonFormat.writeGeometry(olGeometry)
  const geom = parser.read(geojsonGeometry)
  return geojsonFormat.readGeometry(parser.write(toValidGeom(geom)))
}
